const MARKDOWN_COPY_SOURCE_ATTR = "data-markdown-copy-source";

interface SourceLine {
  text: string;
  start: number;
  end: number;
}

interface VisibleCharSource {
  sourceIndex: number;
  lineIndex: number;
}

interface VisibleLine {
  visibleStart: number;
  visibleEnd: number;
  sourceStart: number;
  sourceEnd: number;
  forceWholeLine: boolean;
}

interface VisibleSourceMap {
  visible: string;
  charSources: Array<VisibleCharSource | null>;
  lines: VisibleLine[];
}

interface NormalizedTextMap {
  text: string;
  map: number[];
}

interface RangeTextWithinElement {
  selectedText: string;
  textBefore: string;
  preferExactSource: boolean;
  range: Range;
}

export interface MarkdownSelectionSnippet {
  markdown: string;
  selectedText: string;
  sourceElement: HTMLElement;
  range: Range;
}

const markdownCopySources = new WeakMap<HTMLElement, string>();

export function registerMarkdownCopySource(
  element: HTMLElement,
  source: string,
): () => void {
  element.setAttribute(MARKDOWN_COPY_SOURCE_ATTR, "true");
  markdownCopySources.set(element, source);

  return () => {
    markdownCopySources.delete(element);
    element.removeAttribute(MARKDOWN_COPY_SOURCE_ATTR);
  };
}

export function copyMarkdownSelectionToClipboard(
  event: ClipboardEvent,
  root: HTMLElement,
): boolean {
  if (event.defaultPrevented || !event.clipboardData) {
    return false;
  }

  const snippets = extractMarkdownSnippetsFromSelection(root);
  if (snippets.length === 0) {
    return false;
  }

  event.clipboardData.setData(
    "text/plain",
    snippets.map((snippet) => snippet.markdown).join("\n\n"),
  );
  event.preventDefault();
  return true;
}

export function extractMarkdownSnippetsFromSelection(
  root: HTMLElement,
): MarkdownSelectionSnippet[] {
  const selection = root.ownerDocument.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return [];
  }

  const snippets: MarkdownSelectionSnippet[] = [];
  const selectedTextParts: string[] = [];
  const coveredTextParts: string[] = [];
  const sourceElements = Array.from(
    root.querySelectorAll<HTMLElement>(`[${MARKDOWN_COPY_SOURCE_ATTR}]`),
  );

  for (let rangeIndex = 0; rangeIndex < selection.rangeCount; rangeIndex += 1) {
    const range = selection.getRangeAt(rangeIndex);
    if (!rangeIntersectsNode(range, root)) {
      continue;
    }

    const rootRangeText = getRangeTextWithinElement(range, root);
    if (rootRangeText?.selectedText.trim()) {
      selectedTextParts.push(rootRangeText.selectedText);
    }

    for (const element of sourceElements) {
      if (!rangeIntersectsNode(range, element)) {
        continue;
      }

      const source = markdownCopySources.get(element);
      if (!source) {
        continue;
      }

      const rangeText = getRangeTextWithinElement(range, element);
      if (!rangeText?.selectedText.trim()) {
        continue;
      }
      coveredTextParts.push(rangeText.selectedText);

      const markdown =
        getMarkdownForVisibleSelection(source, rangeText.selectedText, {
          textBefore: rangeText.textBefore,
          preferExactSource: rangeText.preferExactSource,
        }) ?? rangeText.selectedText;
      const normalized = trimBoundaryNewlines(markdown);
      if (normalized.trim()) {
        snippets.push({
          markdown: normalized,
          selectedText: rangeText.selectedText,
          sourceElement: element,
          range: rangeText.range,
        });
      }
    }
  }

  if (
    normalizeSelectedTextForCoverage(selectedTextParts.join("\n")) !==
    normalizeSelectedTextForCoverage(coveredTextParts.join("\n"))
  ) {
    return [];
  }

  return snippets;
}

export function getMarkdownSnippetForElement(
  element: HTMLElement,
): MarkdownSelectionSnippet | null {
  const source = markdownCopySources.get(element);
  if (!source?.trim()) {
    return null;
  }
  const range = createTextContentRange(element);
  return {
    markdown: trimBoundaryNewlines(source),
    selectedText: element.innerText || element.textContent || source,
    sourceElement: element,
    range,
  };
}

/**
 * Recover the markdown for one rendered block (paragraph/list/heading) nested
 * inside a registered copy-source element, so a per-paragraph quote circle can
 * quote just that block. Maps the block's visible text back to its source span
 * via the same visible-source map the copy/selection path uses.
 */
export function getMarkdownSnippetForSubElement(
  sourceElement: HTMLElement,
  blockElement: HTMLElement,
): MarkdownSelectionSnippet | null {
  const source = markdownCopySources.get(sourceElement);
  if (!source?.trim()) {
    return null;
  }
  const selectedText = blockElement.innerText || blockElement.textContent || "";
  if (!selectedText.trim()) {
    return null;
  }
  const doc = blockElement.ownerDocument;
  const range = createTextContentRange(blockElement);

  const beforeRange = doc.createRange();
  beforeRange.selectNodeContents(sourceElement);
  beforeRange.setEnd(range.startContainer, range.startOffset);
  const textBefore = beforeRange.toString();

  const markdown =
    getMarkdownForVisibleSelection(source, selectedText, { textBefore }) ??
    selectedText;
  const normalized = trimBoundaryNewlines(markdown);
  if (!normalized.trim()) {
    return null;
  }
  return {
    markdown: normalized,
    selectedText,
    sourceElement,
    range,
  };
}

function createTextContentRange(element: HTMLElement): Range {
  const range = element.ownerDocument.createRange();
  const walker = element.ownerDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) =>
        node.textContent && node.textContent.length > 0
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT,
    },
  );
  const first = walker.nextNode();
  if (!first) {
    range.selectNodeContents(element);
    return range;
  }

  let last = first;
  for (let next = walker.nextNode(); next; next = walker.nextNode()) {
    last = next;
  }

  range.setStart(first, 0);
  range.setEnd(last, last.textContent?.length ?? 0);
  return range;
}

export function getMarkdownForVisibleSelection(
  source: string,
  selectedText: string,
  options: {
    textBefore?: string;
    preferExactSource?: boolean;
  } = {},
): string | null {
  const normalizedSource = normalizeLineEndings(source);
  const normalizedSelection = normalizeLineEndings(selectedText);
  if (!normalizedSelection.trim()) {
    return null;
  }

  const exactSelection = findExactSourceSelection(
    normalizedSource,
    normalizedSelection,
  );
  if (options.preferExactSource && exactSelection !== null) {
    return exactSelection;
  }

  const sourceMap = buildVisibleSourceMap(normalizedSource);
  const selectionMap = buildVisibleSourceMap(normalizedSelection);
  const sourceMatch = normalizeTextForMatchWithMap(sourceMap.visible);
  const targetMatch = normalizeTextForMatch(selectionMap.visible);
  if (!targetMatch) {
    return exactSelection;
  }

  const preferredStart = options.textBefore
    ? normalizeTextForMatch(buildVisibleSourceMap(options.textBefore).visible)
        .length
    : 0;
  const matchIndex = findBestMatchIndex(
    sourceMatch.text,
    targetMatch,
    preferredStart,
  );
  if (matchIndex === -1) {
    return exactSelection;
  }

  const matchEndIndex = matchIndex + targetMatch.length - 1;
  const visibleStart = sourceMatch.map[matchIndex];
  const visibleEndChar = sourceMatch.map[matchEndIndex];
  if (visibleStart === undefined || visibleEndChar === undefined) {
    return exactSelection;
  }

  const visibleEnd = visibleEndChar + 1;
  let sourceStart = Number.POSITIVE_INFINITY;
  let sourceEnd = -1;
  const touchedLineIndexes = new Set<number>();

  for (
    let visibleIndex = visibleStart;
    visibleIndex < visibleEnd && visibleIndex < sourceMap.charSources.length;
    visibleIndex += 1
  ) {
    const charSource = sourceMap.charSources[visibleIndex];
    if (!charSource) {
      continue;
    }
    sourceStart = Math.min(sourceStart, charSource.sourceIndex);
    sourceEnd = Math.max(sourceEnd, charSource.sourceIndex + 1);
    touchedLineIndexes.add(charSource.lineIndex);
  }

  if (!Number.isFinite(sourceStart) || sourceEnd < sourceStart) {
    return exactSelection;
  }

  for (const lineIndex of touchedLineIndexes) {
    const line = sourceMap.lines[lineIndex];
    if (!line?.forceWholeLine) {
      continue;
    }
    sourceStart = Math.min(sourceStart, line.sourceStart);
    sourceEnd = Math.max(sourceEnd, line.sourceEnd);
  }

  return trimBoundaryNewlines(normalizedSource.slice(sourceStart, sourceEnd));
}

function findExactSourceSelection(
  source: string,
  selectedText: string,
): string | null {
  if (source.includes(selectedText)) {
    return selectedText;
  }

  const trimmed = trimBoundaryNewlines(selectedText);
  if (trimmed !== selectedText && source.includes(trimmed)) {
    return trimmed;
  }

  return null;
}

function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n?/g, "\n");
}

function trimBoundaryNewlines(value: string): string {
  return normalizeLineEndings(value).replace(/^\n+|\n+$/g, "");
}

function normalizeSelectedTextForCoverage(value: string): string {
  return normalizeLineEndings(value).replace(/\s+/g, " ").trim();
}

function rangeIntersectsNode(range: Range, node: Node): boolean {
  try {
    return range.intersectsNode(node);
  } catch {
    return false;
  }
}

function getRangeTextWithinElement(
  range: Range,
  element: HTMLElement,
): RangeTextWithinElement | null {
  const elementRange = element.ownerDocument.createRange();
  elementRange.selectNodeContents(element);
  if (!rangeIntersectsNode(range, element)) {
    return null;
  }

  const clippedRange = range.cloneRange();
  if (range.compareBoundaryPoints(Range.START_TO_START, elementRange) < 0) {
    clippedRange.setStart(
      elementRange.startContainer,
      elementRange.startOffset,
    );
  }
  if (range.compareBoundaryPoints(Range.END_TO_END, elementRange) > 0) {
    clippedRange.setEnd(elementRange.endContainer, elementRange.endOffset);
  }

  const beforeRange = element.ownerDocument.createRange();
  beforeRange.setStart(elementRange.startContainer, elementRange.startOffset);
  beforeRange.setEnd(clippedRange.startContainer, clippedRange.startOffset);

  const sourceModeElements = Array.from(
    element.querySelectorAll<HTMLElement>(".text-block-source"),
  );

  return {
    selectedText: clippedRange.toString(),
    textBefore: beforeRange.toString(),
    preferExactSource: sourceModeElements.some((sourceElement) =>
      rangeIntersectsNode(clippedRange, sourceElement),
    ),
    range: clippedRange,
  };
}

function splitSourceLines(source: string): SourceLine[] {
  const lines = source.split("\n");
  let start = 0;
  return lines.map((text) => {
    const end = start + text.length;
    const line = { text, start, end };
    start = end + 1;
    return line;
  });
}

function buildVisibleSourceMap(source: string): VisibleSourceMap {
  const normalizedSource = normalizeLineEndings(source);
  const sourceLines = splitSourceLines(normalizedSource);
  const lines: VisibleLine[] = [];
  const charSources: Array<VisibleCharSource | null> = [];
  let visible = "";

  for (let lineIndex = 0; lineIndex < sourceLines.length; lineIndex += 1) {
    const sourceLine = sourceLines[lineIndex];
    if (!sourceLine) {
      continue;
    }

    const visibleStart = visible.length;
    const lineMap = buildVisibleLineMap(sourceLine.text, sourceLine.start);
    visible += lineMap.visible;
    charSources.push(
      ...lineMap.charSources.map((sourceIndex) => ({
        sourceIndex,
        lineIndex,
      })),
    );
    const visibleEnd = visible.length;
    lines.push({
      visibleStart,
      visibleEnd,
      sourceStart: sourceLine.start,
      sourceEnd: sourceLine.end,
      forceWholeLine: lineMap.forceWholeLine,
    });

    if (lineIndex < sourceLines.length - 1) {
      visible += "\n";
      charSources.push({ sourceIndex: sourceLine.end, lineIndex });
    }
  }

  return { visible, charSources, lines };
}

function buildVisibleLineMap(
  line: string,
  sourceLineStart: number,
): {
  visible: string;
  charSources: number[];
  forceWholeLine: boolean;
} {
  const blockPrefix = getMarkdownBlockPrefix(line);
  const visibleParts: string[] = [];
  const charSources: number[] = [];

  const appendSourceChar = (index: number) => {
    visibleParts.push(line[index] ?? "");
    charSources.push(sourceLineStart + index);
  };

  for (let index = blockPrefix.contentStart; index < line.length; index += 1) {
    const escapedIndex = getEscapedMarkdownCharIndex(line, index);
    if (escapedIndex !== null) {
      appendSourceChar(escapedIndex);
      index = escapedIndex;
      continue;
    }

    const link = getInlineLinkSpan(line, index);
    if (link) {
      for (
        let textIndex = link.textStart;
        textIndex < link.textEnd;
        textIndex += 1
      ) {
        appendSourceChar(textIndex);
      }
      index = link.end;
      continue;
    }

    if (isInlineMarkdownDelimiter(line[index] ?? "")) {
      continue;
    }

    appendSourceChar(index);
  }

  return {
    visible: visibleParts.join(""),
    charSources,
    forceWholeLine: blockPrefix.forceWholeLine,
  };
}

function getMarkdownBlockPrefix(line: string): {
  contentStart: number;
  forceWholeLine: boolean;
} {
  let contentStart = line.match(/^[ \t]{0,3}/)?.[0].length ?? 0;
  let forceWholeLine = false;

  while (contentStart < line.length) {
    const rest = line.slice(contentStart);
    const blockquote = /^>\s?/.exec(rest);
    if (blockquote) {
      contentStart += blockquote[0].length;
      forceWholeLine = true;
      continue;
    }

    const heading = /^#{1,6}(?:\s+|$)/.exec(rest);
    if (heading) {
      contentStart += heading[0].length;
      forceWholeLine = true;
      continue;
    }

    const taskList = /^[-+*]\s+\[[ xX]\]\s+/.exec(rest);
    if (taskList) {
      contentStart += taskList[0].length;
      forceWholeLine = true;
      continue;
    }

    const orderedList = /^\d{1,9}[.)]\s+/.exec(rest);
    if (orderedList) {
      contentStart += orderedList[0].length;
      forceWholeLine = true;
      continue;
    }

    const unorderedList = /^(?:[-+*]|[•‣⁃])\s+/.exec(rest);
    if (unorderedList) {
      contentStart += unorderedList[0].length;
      forceWholeLine = true;
      continue;
    }

    break;
  }

  return { contentStart, forceWholeLine };
}

function getEscapedMarkdownCharIndex(
  line: string,
  index: number,
): number | null {
  if (line[index] !== "\\") {
    return null;
  }
  const nextIndex = index + 1;
  if (nextIndex >= line.length) {
    return null;
  }
  return "\\`*_{}[]()#+-.!~|>".includes(line[nextIndex] ?? "")
    ? nextIndex
    : null;
}

function getInlineLinkSpan(
  line: string,
  index: number,
): { textStart: number; textEnd: number; end: number } | null {
  const hasImageBang = line[index] === "!" && line[index + 1] === "[";
  const bracketIndex = hasImageBang ? index + 1 : index;
  if (line[bracketIndex] !== "[") {
    return null;
  }

  const textEnd = line.indexOf("]", bracketIndex + 1);
  if (textEnd === -1 || line[textEnd + 1] !== "(") {
    return null;
  }

  const urlEnd = line.indexOf(")", textEnd + 2);
  if (urlEnd === -1) {
    return null;
  }

  return {
    textStart: bracketIndex + 1,
    textEnd,
    end: urlEnd,
  };
}

function isInlineMarkdownDelimiter(char: string): boolean {
  return char === "`" || char === "*" || char === "_" || char === "~";
}

function normalizeTextForMatch(value: string): string {
  return normalizeTextForMatchWithMap(value).text;
}

function normalizeTextForMatchWithMap(value: string): NormalizedTextMap {
  const normalized = normalizeLineEndings(value).replace(/\u00a0/g, " ");
  const chars: string[] = [];
  const map: number[] = [];

  const removeTrailingHorizontalSpace = () => {
    if (chars[chars.length - 1] === " ") {
      chars.pop();
      map.pop();
    }
  };

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index] ?? "";
    if (char === "\n") {
      removeTrailingHorizontalSpace();
      if (chars.length > 0 && chars[chars.length - 1] !== "\n") {
        chars.push("\n");
        map.push(index);
      }
      continue;
    }

    if (/\s/.test(char)) {
      if (chars.length > 0) {
        const previous = chars[chars.length - 1];
        if (previous !== " " && previous !== "\n") {
          chars.push(" ");
          map.push(index);
        }
      }
      continue;
    }

    chars.push(char);
    map.push(index);
  }

  while (chars.length > 0 && /\s/.test(chars[chars.length - 1] ?? "")) {
    chars.pop();
    map.pop();
  }

  return { text: chars.join(""), map };
}

function findBestMatchIndex(
  source: string,
  target: string,
  preferredStart: number,
): number {
  let fallback = -1;
  let searchFrom = 0;
  const minimumPreferredStart = Math.max(0, preferredStart - 2);

  while (searchFrom <= source.length) {
    const index = source.indexOf(target, searchFrom);
    if (index === -1) {
      break;
    }
    if (fallback === -1) {
      fallback = index;
    }
    if (index >= minimumPreferredStart) {
      return index;
    }
    searchFrom = index + Math.max(1, target.length);
  }

  return fallback;
}
